/**
 * 데이터처리 노드 점검 — 저장·실행 전에 논리적으로 어긋난 곳을 찾아 알려 준다.
 *
 * 화면(TransformLoadPanel)의 '점검' 칸과 캔버스 노드의 경고가 같은 판정을 쓴다.
 * 실행해 봐야 드러나는 문제를 설계 단계에서 보여 주는 것이 목적이다 — 현장에서 실제로
 *  · 문자열이 DATE 컬럼에 들어가 ORA-01861,
 *  · 테이블 컬럼이 바뀌었는데 매핑은 옛 이름을 가리킴,
 * 같은 일이 실행 뒤에야 드러났다.
 *
 * 단계
 *  - error : 이대로면 실행이 실패하거나 데이터가 사라진다
 *  - warn  : 실행은 되지만 의도와 다를 가능성이 크다(빈 값 적재, 안 옮겨지는 항목 등)
 *  - info  : 정보가 부족해 일부 점검을 건너뛰었다
 */
import type { Column, Endpoint } from './transformLoad';
import i18n from '../../../i18n';

const tr = (key: string, opts?: Record<string, unknown>) => String(i18n.t(`workflow:check.${key}`, opts as any));

export type CheckLevel = 'error' | 'warn' | 'info';
/** code — 화면이 판정에 쓰는 값(문구로 판정하지 않는다). message 는 표시용 번역 문구 */
export interface CheckItem { level: CheckLevel; code: string; message: string }

export interface MappingRow { target: string; source: string; expr: string }

/** DB테이블 노드가 컬럼을 다시 읽었을 때 남기는 변경 기록 */
export interface ColumnsChange { at: string; added: string[]; removed: string[]; typeChanged: string[] }

const up = (s: string) => (s || '').toUpperCase();
const LIST_MAX = 5;
const listOf = (names: string[]) => (names.length > LIST_MAX ? tr('more', { list: names.slice(0, LIST_MAX).join(', '), n: names.length - LIST_MAX }) : names.join(', '));

const isDateType = (t?: string) => /date|time/i.test(t || '');
const isNumType = (t?: string) => /num|int|dec|float|double|real|money|serial/i.test(t || '');
const isCharType = (t?: string) => /char|text|clob|string|varchar/i.test(t || '');

/** 두 항목 자료형이 명백히 다른 계열인가 — 문자 → 날짜/숫자만 본다(가장 흔한 실패) */
function riskyConversion(src?: Column, tgt?: Column): string {
  if (!src?.dataType || !tgt?.dataType) return '';
  if (isCharType(src.dataType) && isDateType(tgt.dataType)) return tr('charToDate');
  if (isCharType(src.dataType) && isNumType(tgt.dataType)) return tr('charToNum');
  return '';
}

export interface CheckInput {
  source: Endpoint | null;
  target: Endpoint | null;
  sourceColumns: Column[];
  targetColumns: Column[];
  cfg: Record<string, any>;
}

export function checkTransformLoad({ source, target, sourceColumns, targetColumns, cfg }: CheckInput): CheckItem[] {
  const out: CheckItem[] = [];
  const add = (level: CheckLevel, code: string, opts?: Record<string, unknown>) => out.push({ level, code, message: tr(code, opts) });

  if (!source) add('error', 'noSource');
  if (!target) add('error', 'noTarget');

  const queryMode = source?.kind === 'db' && cfg.extractMode === 'QUERY';
  if (queryMode && !(cfg.query || '').trim()) add('error', 'emptyQuery');

  const srcKnown = sourceColumns.length > 0 && !queryMode;   // SQL 쿼리면 결과 항목을 미리 알 수 없다
  const tgtKnown = targetColumns.length > 0;
  const srcByName = new Map(sourceColumns.map((c) => [up(c.name), c]));
  const tgtByName = new Map(targetColumns.map((c) => [up(c.name), c]));
  const toDb = target?.kind === 'db';

  // ── 같은 테이블을 읽고 비우고 쓰는가
  if (source?.kind === 'db' && target?.kind === 'db' && (cfg.mode || 'WRITE') === 'WRITE') {
    const s = source.node?.data?.config || {};
    const t = target.node?.data?.config || {};
    if (s.connection && s.connection === t.connection && up(s.table) === up(t.table) && up(s.schema || '') === up(t.schema || '')) {
      add('error', 'sameTable');
    }
  }

  // ── UPSERT 키
  if (toDb && cfg.mode === 'UPSERT') {
    const keys: string[] = Array.isArray(cfg.keyColumns) ? cfg.keyColumns : [];
    if (!keys.length) add('error', 'noUpsertKey');
    else if (tgtKnown) {
      const missing = keys.filter((k) => !tgtByName.has(up(k)));
      if (missing.length) add('error', 'upsertKeyMissing', { list: listOf(missing) });
    }
  }

  if (cfg.transformMode === 'MAPPING') {
    const rows: MappingRow[] = (Array.isArray(cfg.mapping) ? cfg.mapping : []).filter((m: MappingRow) => m && m.target);
    if (!rows.length) {
      add('error', 'noMapping');
    } else {
      const dup = rows.map((r) => up(r.target)).filter((t, i, a) => a.indexOf(t) !== i);
      if (dup.length) add('error', 'dupTarget', { list: listOf(Array.from(new Set(dup))) });
      if (tgtKnown && target?.kind !== 'inner') {
        const gone = rows.filter((r) => !tgtByName.has(up(r.target))).map((r) => r.target);
        if (gone.length) add('error', 'goneTarget', { list: listOf(gone) });
        const unmapped = targetColumns.filter((c) => !rows.some((r) => up(r.target) === up(c.name))).map((c) => c.name);
        if (unmapped.length) add('warn', 'unmappedTarget', { n: unmapped.length, list: listOf(unmapped) });
      }
      if (srcKnown) {
        const gone = rows.filter((r) => r.source && !(r.expr || '').trim() && !srcByName.has(up(r.source))).map((r) => r.source);
        if (gone.length) add('error', 'goneSource', { list: listOf(gone) });
      }
      const empty = rows.filter((r) => !r.source && !(r.expr || '').trim()).map((r) => r.target);
      if (empty.length) add('error', 'emptyMapping', { list: listOf(empty) });
      if (srcKnown && tgtKnown) {
        const risky = rows
          .filter((r) => r.source && !(r.expr || '').trim())
          .map((r) => ({ r, kind: riskyConversion(srcByName.get(up(r.source)), tgtByName.get(up(r.target))) }))
          .filter((x) => x.kind);
        if (risky.length) add('warn', 'riskyMapping', { list: listOf(risky.map((x) => `${x.r.source}→${x.r.target}(${x.kind})`)) });
      }
    }
  } else if (srcKnown && tgtKnown && target?.kind !== 'inner') {
    // Move — 같은 이름끼리 옮긴다
    const common = sourceColumns.filter((c) => tgtByName.has(up(c.name)));
    if (!common.length) {
      add('error', 'noCommon');
    } else {
      const notInTarget = sourceColumns.filter((c) => !tgtByName.has(up(c.name))).map((c) => c.name);
      if (notInTarget.length) add('warn', 'notInTarget', { n: notInTarget.length, list: listOf(notInTarget) });
      if (toDb) {
        const notInSource = targetColumns.filter((c) => !srcByName.has(up(c.name))).map((c) => c.name);
        if (notInSource.length) add('warn', 'notInSource', { n: notInSource.length, list: listOf(notInSource) });
      }
      const risky = common.map((c) => ({ c, kind: riskyConversion(c, tgtByName.get(up(c.name))) })).filter((x) => x.kind);
      if (risky.length) add('warn', 'riskyMove', { list: listOf(risky.map((x) => `${x.c.name}(${x.kind})`)) });
    }
  }

  // ── 컬럼이 바뀐 뒤 매핑을 다시 확인하지 않았는가
  const synced = Date.parse(cfg.mappingSyncedAt || '') || 0;
  ([['source', source], ['target', target]] as const).forEach(([which, ep]) => {
    const ch: ColumnsChange | undefined = ep?.kind === 'db' ? ep.node?.data?.config?.columnsChange : undefined;
    if (!ch || !(Date.parse(ch.at) > synced)) return;
    const parts = [
      ch.added?.length ? tr('added', { list: listOf(ch.added) }) : '',
      ch.removed?.length ? tr('removed', { list: listOf(ch.removed) }) : '',
      ch.typeChanged?.length ? tr('typeChanged', { list: listOf(ch.typeChanged) }) : '',
    ].filter(Boolean);
    if (parts.length) add('warn', 'columnsChanged', { which: tr(which), parts: parts.join(' · '), action: cfg.transformMode === 'MAPPING' ? tr('recheckMapping') : tr('recheckMove') });
  });

  if (source && source.kind !== 'inner' && !sourceColumns.length && !queryMode) add('info', 'noSourceInfo');
  if (target && target.kind !== 'inner' && !tgtKnown) add('info', 'noTargetInfo');
  if (queryMode) add('info', 'queryInfo');

  return out;
}
